import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import useHttpLoader from 'shared/hooks/useHttpLoader'
import protectionsApi from 'screens/Protection/protection.api'
import { protectionAtom } from 'screens/Protection/protection.atom'
import { IProtectionDayResponse } from 'screens/Protection/protection.types'
import { formatDateToApi } from '../../../../../utils/formatDate'
import { useCalendar } from 'screens/Protection/hooks/useCalendar'

import Calendar from 'screens/Protection/components/Calendar/Calendar'
import ContextMenu from './contextMenu/ContextMenu'
import DefenceModal from '../../../components/modal/DefenceModal'

export default function ScheduleProtection() {
  const { wait } = useHttpLoader()
  const navigate = useNavigate()

  const setCalendarState = useSetRecoilState(protectionAtom)
  const calendarState = useRecoilValue(protectionAtom)

  const [currentDate, setCurrentDate] = useState(new Date())

  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    date: Date | null
  } | null>(null)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  useEffect(() => {
    const from = new Date(year, month, 1)
    const to = new Date(year, month + 1, 0)

    wait(
      protectionsApi.listProtectionDays({
        from: formatDateToApi(from),
        to: formatDateToApi(to),
      }),
      (resp) => {
        if (resp.status === 'success') {
          setCalendarState((prev) => ({
            ...prev,
            items: resp.body.items,
          }))
        }
      }
    )
  }, [year, month])

  const protectionsByDate = useMemo(() => {
    const map = new Map<string, IProtectionDayResponse[]>()

    calendarState.items.forEach((item) => {
      const key = item.startsAt.split('T')[0]

      const existing = map.get(key) || []

      map.set(key, [...existing, item])
    })

    return map
  }, [calendarState.items])

  const { weeks, isToday } = useCalendar<IProtectionDayResponse>({
    year,
    month,
    itemsByDate: protectionsByDate,
  })

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1))
  }

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1))
  }

  useEffect(() => {
    const closeMenu = () => setContextMenu(null)

    document.addEventListener('mousedown', closeMenu)

    return () => document.removeEventListener('mousedown', closeMenu)
  }, [])

  const handleDayClick = (e: React.MouseEvent, date: Date) => {
    e.stopPropagation()

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      date,
    })
  }

  const handleRedirect = (id: IProtectionDayResponse['id']) => {
    navigate(`/admin/protection-admin/${id}`)
  }

  return (
    <>
      <Calendar
        year={year}
        month={month}
        weeks={weeks}
        isToday={isToday}
        onPrevMonth={prevMonth}
        onNextMonth={nextMonth}
        handleRedirect={handleRedirect}
        onDayClick={handleDayClick}
      />

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          onCreate={() => {
            setSelectedDate(contextMenu.date)
            setIsCreateModalOpen(true)
            setContextMenu(null)
          }}
        />
      )}

      {isCreateModalOpen && (
        <DefenceModal
          type='create'
          date={selectedDate}
          onClose={() => {
            setIsCreateModalOpen(false)
            setSelectedDate(null)
          }}
        />
      )}
    </>
  )
}
